import type { FunctionalComponent, VNode } from 'vue';
import {
  Form,
  FormItem,
  Input,
  type FormProps,
  Textarea,
  Select,
  RadioGroup,
  CheckboxGroup,
} from 'ant-design-vue';

interface Props {
  [key: string]: any;
}

interface column {
  key: string;
  formItem?: Props;
  inputAttrs?: Props;
  render?: (model: Props) => any;
}

const getPlaceholderLabel = (item: Props) => {
  const { $t } = useNuxtApp();
  return (
    (item.type === 'array' || item.inputAttrs.type === 'select'
      ? $t('qing-xuan-ze')
      : $t('qing-shu-ru')) + item.formItem.label
  );
};

export interface ColumnsItem {
  key: string;
  type?: string;
  inputAttrs?: {
    type?: 'input' | 'select' | 'textarea' | 'radio' | 'checkbox';
    placeholder?: string;
    defaultValue?: any;
    [key: string]: any;
  };
  formItem?: {
    label?: string;
    name?: string;
    rules?: any[];
    [key: string]: any;
  };
  render?: (row: any, record: any) => VNode;
  visible?: (row: any) => boolean;
}

const inputComponents: Props = {
  input: Input,
  textarea: Textarea,
  select: Select,
  radio: RadioGroup,
  checkbox: CheckboxGroup,
};

export const FormItemView: FunctionalComponent<{
  type?: string;
  [k: string]: any;
}> = (props, { attrs }) => {
  const Item = inputComponents[props.type || 'input'] || Input;
  return <Item {...attrs} />;
};

export const FormView: FunctionalComponent<{
  columns: ColumnsItem[];
  model: Props;
  validateInfos?: Props;
  formAttrs?: FormProps;
}> = props => {
  const formAttrs: FormProps = Object.assign(
    {
      labelAlign: 'left',
      labelCol: { span: 6 },
      wrapperCol: { span: 18 },
      colon: false,
      autocomplete: 'off',
    },
    props.formAttrs,
  );

  const { validateInfos, model, columns } = props;

  const items = columns.filter(item => (item?.visible ? item?.visible(model) : true));

  return (
    <Form {...formAttrs}>
      {items.map(item => (
        <FormItem {...item.formItem} {...validateInfos?.[item.key]}>
          {item.render ? (
            item.render(model, item)
          ) : (
            <FormItemView
              {...item.inputAttrs}
              value={model[item.key] as any}
              onUpdate:value={(v: any) => (model[item.key] = v)}
            />
          )}
        </FormItem>
      ))}
    </Form>
  );
};

export const useForm = (columns: ColumnsItem[], formAttrs?: FormProps) => {
  const modelRef = reactive<Props>({});
  const rulesRef = reactive<Props>({});

  columns.forEach((item: Props) => {
    modelRef[item.key] = item?.inputAttrs?.defaultValue || undefined;
    if (item.inputAttrs && !item.inputAttrs.placeholder) {
      item.inputAttrs.placeholder = getPlaceholderLabel(item);
    }

    if (item.formItem.rules && (!item?.visible || item?.visible(modelRef))) {
      rulesRef[item.key] = item.formItem.rules;
    }
  });

  const { resetFields, validate, validateInfos } = Form.useForm(modelRef, rulesRef);

  watchEffect(() => {
    columns.forEach((item: Props) => {
      if (item.visible) {
        rulesRef[item.key] = item.visible(modelRef) ? item.formItem.rules : undefined;
      }
    });
  });

  const View: FunctionalComponent = () => (
    <FormView
      model={modelRef}
      validateInfos={validateInfos}
      columns={columns}
      formAttrs={formAttrs}
    />
  );

  return {
    resetFields,
    validate,
    modelRef,
    View,
  };
};
